import { GeneralHelpers } from '../../helpers/general.helper';
import { RuntimeResult } from '../../interfaces/runtime.interface';
import { Chunk } from '../../interfaces/step/step.interface';

import { FILE_EXTENSIONS } from '../../constants/general.constants';
import { FileModule } from '../../interfaces/file.interface';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import { ExceutionHelperError } from '../../interfaces/errors.interface';
import { SimpleUIService } from '../simple-ui/simple-ui.service';
import { StepDataHelper } from '../../helpers/step-data.helper';
import { FileService } from '../file/file.service';

export class JavaScriptHelper {
  // Most `any` in this helper are the QuickJS runtime boundary: `context` is a
  // quickjs-emscripten VM handle (whose asyncified surface its own types only
  // loosely describe), and `value`/`handle`/`args`/`result` are arbitrary JS
  // values crossing into/out of that VM. These are kept `any` (partly inherent
  // per the audit; the JS-bridge boundary is revisited with the sandbox work,
  // ADR-0005) — the same treatment as python-ehq-bridge's Python handlers.
  public static addFetchToContext(context: any): any {
    const fetchFunction = context.newAsyncifiedFunction(
      'fetch',
      async (url: any, options: any = {}) => {
        try {
          // Convert QuickJS objects to normal JS objects
          const config = {
            method: options.method || 'GET',
            headers: options.headers ? context.dump(options.headers) : {},
            body: options.body ? context.dump(options.body) : undefined,
          };

          // Perform the native fetch
          const nativeResponse = await fetch(context.dump(url), config);
          // Create a Response-like object
          const responseObj = context.newObject();

          // Recursively copy all properties from Response
          this.copyObjectToContext(context, responseObj, nativeResponse);

          // Add headers object (special handling for Headers object)
          const headersObj = context.newObject();
          nativeResponse.headers.forEach((value, key) => {
            context.setProp(headersObj, key, context.newString(value));
          });
          context.setProp(responseObj, 'headers', headersObj);

          // Clone the response for reading the body
          const clonedResponse = nativeResponse.clone();

          // Try to extract and add body content
          try {
            const contentType = nativeResponse.headers.get('content-type') || '';

            if (contentType.includes('application/json')) {
              const jsonData = await clonedResponse.json();
              context.setProp(responseObj, 'bodyContent',
                JavaScriptHelper.createContextEntity(context, jsonData));
            } else if (contentType.includes('text/')) {
              const textData = await clonedResponse.text();
              context.setProp(responseObj, 'bodyContent',
                context.newString(textData));
            } else {
              // For binary content, add info but not full content
              const blob = await clonedResponse.blob();
              const bodyInfo = context.newObject();
              context.setProp(bodyInfo, 'type', context.newString(blob.type));
              context.setProp(bodyInfo, 'size', blob.size);
              context.setProp(responseObj, 'bodyContent', bodyInfo);
            }
          } catch (error) {
            context.setProp(
              responseObj,
              'bodyError',
              context.newString(`Could not read body: ${GeneralHelpers.errorMessage(error)}`)
            );
          }

          // Add response methods
          const textMethod = context.newAsyncifiedFunction('text', async () => {
            try {
              const text = await nativeResponse.text();
              return context.newString(text);
            } catch (error) {
              throw context.newError('ResponseError',
                `Failed to read text: ${GeneralHelpers.errorMessage(error)}`);
            }
          });
          context.setProp(responseObj, 'text', textMethod);

          const jsonMethod = context.newAsyncifiedFunction('json', async () => {
            try {
              const json = await nativeResponse.json();
              return JavaScriptHelper.createContextEntity(context, json);
            } catch (error) {
              throw context.newError('ResponseError',
                `Failed to parse JSON: ${GeneralHelpers.errorMessage(error)}`);
            }
          });
          context.setProp(responseObj, 'json', jsonMethod);

          const arrayBufferMethod = context.newAsyncifiedFunction('arrayBuffer', async () => {
            try {
              const buffer = await nativeResponse.arrayBuffer();
              const uint8Array = new Uint8Array(buffer);
              return JavaScriptHelper.createContextEntity(context, Array.from(uint8Array));
            } catch (error) {
              throw context.newError('ResponseError',
                `Failed to read arrayBuffer: ${GeneralHelpers.errorMessage(error)}`);
            }
          });
          context.setProp(responseObj, 'arrayBuffer', arrayBufferMethod);

          return responseObj;
        } catch (error) {
          console.error('Fetch Error:', error);
          throw context.newError(
            'FetchError',
            `Failed to fetch: ${GeneralHelpers.errorMessage(error)}`
          );
        }
      }
    );

    context.setProp(context.global, 'fetch', fetchFunction);
    fetchFunction.dispose();
    return context;
  }

  private static copyObjectToContext(context: any, targetObj: any, sourceObj: any): void {
    // Get all own properties (including non-enumerable ones)
    const properties = Object.getOwnPropertyNames(sourceObj);

    // Get all properties from the prototype chain
    let prototype = Object.getPrototypeOf(sourceObj);
    while (prototype && prototype !== Object.prototype) {
      properties.push(...Object.getOwnPropertyNames(prototype)
        .filter(prop => !properties.includes(prop)));
      prototype = Object.getPrototypeOf(prototype);
    }

    // Add each property to the QuickJS object
    for (const prop of properties) {
      try {
        // Skip methods, functions, and specific properties we handle separately
        if (prop === 'constructor' || prop === 'headers' ||
          prop === 'body' || prop === 'json' ||
          prop === 'text' || prop === 'arrayBuffer' ||
          prop === 'blob' || prop === 'formData' ||
          typeof sourceObj[prop] === 'function') {
          continue;
        }

        const value = sourceObj[prop];

        // Use createContextEntity to handle all value types properly
        if (value !== undefined && value !== null) {
          const contextValue = JavaScriptHelper.createContextEntity(context, value);
          context.setProp(targetObj, prop, contextValue);
          // Dispose of the context value if it's a disposable object
          if (contextValue && typeof contextValue.dispose === 'function') {
            contextValue.dispose();
          }
        }
      } catch (error) {
        // Skip properties that can't be accessed or copied
        console.debug(`Could not copy property ${prop}:`, error);
      }
    }
  }

  /**
   * Convert a native JS return value into a QuickJS handle.
   * Preserves null (import methods like getConnection / capturePhoto cancel).
   */
  public static toQuickJsValue(context: any, value: any): any {
    if (value === null) {
      const nullResult = context.evalCode('null');
      if (nullResult.error) {
        nullResult.error.dispose();
        throw new Error('Failed to create null value in QuickJS context');
      }
      return nullResult.value;
    }
    return JavaScriptHelper.createContextEntity(context, value);
  }

  public static createContextEntity(context: any, value: any): any {
    const type = GeneralHelpers.trueTypeOf(value);
    let result: any;

    switch (type) {
      case 'number':
        result = context.newNumber(value);
        break;
      case 'string':
        result = context.newString(value);
        break;
      case 'array':
        result = this.interpolateIterableData(
          context,
          context.newArray(),
          value
        );
        break;
      case 'object':
        result = this.interpolateIterableData(
          context,
          context.newObject(),
          value
        );
        break;
      case 'function':
        result = context.newFunction(value);
        break;
      case 'uint32Array':
      case 'uint16array':
      case 'uint8array':
      case 'arraybuffer': {
        // Use newArrayBuffer() for efficient binary data transfer (SCRUM-246).
        // Previous approach created one JSValue per byte — a 3 MB file produced
        // 3.1 M QuickJS objects (~50 MB), hitting the runtime memory limit.
        // newArrayBuffer() stores data as a single contiguous WASM block.
        let bytes: ArrayBuffer;
        if (value instanceof ArrayBuffer) {
          bytes = value;
        } else {
          // TypedArray (Uint8Array, Uint16Array, Uint32Array) — extract underlying buffer slice
          const view = value instanceof Uint8Array ? value
            : value instanceof Uint16Array ? value
            : value instanceof Uint32Array ? value
            : new Uint8Array(value);
          bytes = view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
        }
        // Wrap ArrayBuffer in Uint8Array inside QuickJS so user code sees
        // .constructor.name === "Uint8Array" and .length (matching enoch behavior).
        // Note: callFunction() is JS_Call (regular call), NOT a constructor call.
        // Uint8Array() without `new` throws TypeError, so we use evalCode.
        const ab = context.newArrayBuffer(bytes);
        context.setProp(context.global, '__ehq_tmp', ab);
        const wrapped = context.evalCode('new Uint8Array(__ehq_tmp)');
        // Clean up temp global — dispose evalCode result to avoid JSValue leak
        const cleanup = context.evalCode('delete __ehq_tmp');
        if (cleanup.error) cleanup.error.dispose(); else cleanup.value.dispose();
        ab.dispose();
        if (wrapped.error) {
          wrapped.error.dispose();
          // Fallback: return raw ArrayBuffer (still avoids OOM)
          result = context.newArrayBuffer(bytes);
        } else {
          result = wrapped.value;
        }
        break;
      }
      // Boolean is a string presentation of a boolean value in quickjs
      case 'boolean':
      default:
        result = context.newString(`${value}`);
        break;
    }

    return result;
  }

  /**
   * Dump a QuickJS handle to native JS with efficient binary data extraction.
   *
   * Standard dump() on TypedArrays either iterates all elements (slow for large
   * buffers) or returns toString(). This method first attempts to extract binary
   * data via getArrayBuffer(), which copies the contiguous WASM memory block in
   * one shot. Falls back to standard dump() for non-binary types.
   */
  public static dumpBinaryAware(context: any, handle: any): any {
    // 1. Try direct ArrayBuffer extraction (for raw ArrayBuffer handles)
    try {
      const buf = context.getArrayBuffer(handle);
      if (buf?.value) {
        const copy = new Uint8Array(buf.value);
        buf.dispose();
        return copy;
      }
    } catch {
      // Not an ArrayBuffer — try TypedArray path
    }

    // 2. Try TypedArray.buffer extraction (for Uint8Array/Uint16Array/etc.)
    try {
      const bufferProp = context.getProp(handle, 'buffer');
      if (bufferProp) {
        try {
          const buf = context.getArrayBuffer(bufferProp);
          if (buf?.value) {
            const copy = new Uint8Array(buf.value);
            buf.dispose();
            bufferProp.dispose();
            return copy;
          }
        } catch {
          // .buffer exists but isn't an ArrayBuffer
        }
        bufferProp.dispose();
      }
    } catch {
      // No .buffer property — fall through to standard dump
    }

    return context.dump(handle);
  }

  public static addConsoleLog(context: any, chunkContext: ChunkContext): any {
    const logHandle = context.newFunction('log', (...args: any) => {
      const nativeArgs = args.map(context.dump);
      console.log('console.log', ...nativeArgs);
      // EHQ-110: pass structured args through addOutput (objects/arrays stay structured)
      chunkContext.addOutput(
        JavaScriptHelper.formatConsoleOutputArgs(nativeArgs),
        'success',
        'javascript',
        true
      );
    });

    const infoHandle = context.newFunction('info', (...args: any) => {
      const nativeArgs = args.map(context.dump);
      console.info('console.info', ...nativeArgs);
      chunkContext.addOutput(
        JavaScriptHelper.formatConsoleOutputArgs(nativeArgs),
        'success',
        'javascript',
        true
      );
    });

    const warnHandle = context.newFunction('warn', (...args: any) => {
      const nativeArgs = args.map(context.dump);
      console.warn('console.warn', ...nativeArgs);
      chunkContext.addOutput(
        JavaScriptHelper.formatConsoleOutputArgs(nativeArgs),
        'success',
        'javascript',
        true
      );
    });

    const errorHandle = context.newFunction('error', (...args: any) => {
      const nativeArgs = args.map(context.dump);
      console.error('console.error', ...nativeArgs);
      chunkContext.addOutput(
        JavaScriptHelper.formatConsoleOutputArgs(nativeArgs),
        'error',
        'javascript',
        true
      );
    });

    const consoleHandle = context.newObject();
    context.setProp(consoleHandle, 'log', logHandle);
    context.setProp(consoleHandle, 'info', infoHandle);
    context.setProp(consoleHandle, 'warn', warnHandle);
    context.setProp(consoleHandle, 'error', errorHandle);
    context.setProp(context.global, 'console', consoleHandle);

    logHandle.dispose();
    infoHandle.dispose();
    warnHandle.dispose();
    errorHandle.dispose();
    consoleHandle.dispose();

    return context;
  }

  private static interpolateIterableData(
    runtimeContext: any,
    newContext: any,
    value: any
  ): any {
    const setValue = (newContext: any, key: number | string, value: any) => {
      const type = GeneralHelpers.trueTypeOf(value);
      let resultContext;
      let interpolatedContext;

      switch (type) {
        case 'number':
          interpolatedContext = runtimeContext.newNumber(value);
          runtimeContext.setProp(newContext, key, interpolatedContext);
          interpolatedContext.dispose();
          break;
        case 'string':
          interpolatedContext = runtimeContext.newString(value);
          runtimeContext.setProp(newContext, key, interpolatedContext);
          interpolatedContext.dispose();
          break;
        case 'boolean':
          runtimeContext.setProp(newContext, key, value);
          break;
        case 'object':
          interpolatedContext = runtimeContext.newObject();
          resultContext = this.interpolateIterableData(
            runtimeContext,
            interpolatedContext,
            value
          );
          runtimeContext.setProp(newContext, key, resultContext);
          interpolatedContext.dispose();
          break;
        case 'array':
          interpolatedContext = runtimeContext.newArray();
          resultContext = this.interpolateIterableData(
            runtimeContext,
            interpolatedContext,
            value
          );
          runtimeContext.setProp(newContext, key, resultContext);
          interpolatedContext.dispose();
          break;
        case 'uint8array':
        case 'uint16array':
        case 'uint32array':
        case 'arraybuffer':
          resultContext = this.createContextEntity(runtimeContext, value);
          runtimeContext.setProp(newContext, key, resultContext);
          break;
        default:
          interpolatedContext = runtimeContext.newString(`${value}`);
          runtimeContext.setProp(newContext, key, interpolatedContext);
          interpolatedContext.dispose();
          break;
      }
    };

    if (GeneralHelpers.trueTypeOf(value) === 'array') {
      value.forEach((value: any, index: number) => {
        setValue(newContext, index, value);
      });
    }

    if (GeneralHelpers.trueTypeOf(value) === 'object') {
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          setValue(newContext, key, value[key]);
        }
      }
    }

    return newContext;
  }

  /**
   * Formats JavaScript error messages for better readability
   * Removes verbose stack traces and keeps only the essential error message
   */
  public static formatJavaScriptError(errorMessage: string): string {
    if (!errorMessage) return errorMessage;

    // Pattern 1: Extract error from stack trace format
    // "Error: message at ChunkContext.addOutput (url) at ..."
    const stackTraceMatch = errorMessage.match(/^([^@\n]+?)(?:\s+at\s+|\n)/);
    if (stackTraceMatch) {
      let cleanError = stackTraceMatch[1].trim();

      // Remove duplicate "Error:" prefixes
      cleanError = cleanError.replace(/^Error:\s*Error:\s*/, 'Error: ');
      cleanError = cleanError.replace(/^Error:\s*/, '');

      return cleanError;
    }

    // Pattern 2: Remove everything after "at ChunkContext" or similar stack frames
    const atMatch = errorMessage.match(/^(.+?)\s+at\s+(?:ChunkContext|_JavascriptService|Object|Generator|fulfilled|_ZoneDelegate|ZoneImpl|AsyncStackTaggingZoneSpec|IDBRequest)/);
    if (atMatch) {
      let cleanError = atMatch[1].trim();
      cleanError = cleanError.replace(/^Error:\s*Error:\s*/, 'Error: ');
      cleanError = cleanError.replace(/^Error:\s*/, '');
      return cleanError;
    }

    // Pattern 3: Clean up "step execution error: step execution error:" duplication
    errorMessage = errorMessage.replace(/step execution error:\s*step execution error:/gi, '');

    // Pattern 4: Remove "Error: Error:" duplication
    errorMessage = errorMessage.replace(/^Error:\s*Error:\s*/i, '');

    // Pattern 5: If message contains newlines, take only the first meaningful line
    const lines = errorMessage.split('\n');
    if (lines.length > 1) {
      // Find the first non-empty line that doesn't start with whitespace or "at "
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed && !trimmed.startsWith('at ') && !trimmed.startsWith('File ')) {
          return trimmed.replace(/^Error:\s*/, '');
        }
      }
    }

    return errorMessage.replace(/^Error:\s*/, '');
  }

  public static renderError(error: ExceutionHelperError): RuntimeResult {
    if (!error) {
      return {
        value: `${error}`,
        resultStatus: 'error',
      };
    }
    const { message, name, stack } = error;

    // Clean up JavaScript error messages first
    let cleanedMessage = this.formatJavaScriptError(message);

    // Check if the error message contains double quotes, if so, assume it is detailed enough to show to the user w/o the name and stack
    if (cleanedMessage && cleanedMessage.indexOf('"') > 0) {
      return {
        value: cleanedMessage,
        resultStatus: 'error',
      };
    }
    // Only include stack trace if it's available and different from the message
    // Don't duplicate the error name if it's already in the message
    const includeStack = stack && !cleanedMessage.includes(stack);
    const includeName = name && name !== 'step execution error' && !cleanedMessage.includes(name);

    let errorValue = cleanedMessage;
    if (includeName) {
      errorValue += ` (${name})`;
    }
    if (includeStack) {
      errorValue += `\n${stack}`;
    }

    return {
      value: errorValue,
      resultStatus: 'error',
    };
  }

  public static renderSuccess(value: any): RuntimeResult {
    if (value === '' || value === undefined || value === null) {
      return {
        value: `${value}`,
        resultStatus: 'warning',
      };
    }

    // EHQ-110: Keep objects/arrays structured so the output renderer can
    // pretty-print them (prettyjson). Primitives pass through as-is.
    return {
      value,
      resultStatus: 'success',
    };
  }

  /**
   * EHQ-110: Format console.log/info/warn/error arguments for chunk output.
   * - Single arg: returned as-is (objects/arrays stay structured for pretty rendering)
   * - Multiple args: joined; objects/arrays are pretty-printed with 2-space indent
   */
  public static formatConsoleOutputArgs(args: unknown[]): unknown {
    if (args.length === 0) {
      return '';
    }
    if (args.length === 1) {
      return args[0];
    }
    return args.map((arg) => JavaScriptHelper.stringifyConsoleArg(arg)).join(' ');
  }

  private static stringifyConsoleArg(arg: unknown): string {
    if (arg === null || arg === undefined) {
      return String(arg);
    }
    if (typeof arg === 'object') {
      try {
        return JSON.stringify(arg, null, 2);
      } catch {
        return Object.prototype.toString.call(arg);
      }
    }
    return String(arg);
  }

  public static addPrintToContext(context: any, chunkContext: ChunkContext): any {
    const printFunction = context.newFunction('print', (...args: any) => {
      // Print the arguments to the console for visibility
      console.log(...args.map((arg: any) => context.dump(arg)));
      const nativeArgs = args.map((arg: any) => context.dump(arg));
      chunkContext.addOutput(
        JavaScriptHelper.formatConsoleOutputArgs(nativeArgs),
        'success',
        'javascript',
        true
      );
      // Convert and return the arguments as context entities for further use in QuickJS
      if (args.length === 1) {
        // If there is only one argument, return it directly as a context entity
        return JavaScriptHelper.createContextEntity(
          context,
          context.dump(args[0])
        );
      } else {
        // If there are multiple arguments, return them as an array of context entities
        const array = context.newArray();
        args.forEach((arg: any, index: number) => {
          context.setProp(
            array,
            index,
            JavaScriptHelper.createContextEntity(context, context.dump(arg))
          );
        });
        return array;
      }
    });

    context.setProp(context.global, 'print', printFunction);
    printFunction.dispose(); // Dispose of the print function after adding it to global
    return context;
  }

  /**
   * Add getJobArguments() to QuickJS context.
   * In ehq-web (browser), returns the `job_arguments` notebook global when set.
   * Enoch provides the actual job arguments from the trigger payload.
   */
  public static addGetJobArgumentsToContext(
    context: any,
    notebookVariablesService?: {
      variableList?: Array<{ name?: string; value?: unknown }>;
      variableList$?: () => Array<{ name?: string; value?: unknown }>;
    },
  ): any {
    const getJobArgumentsFunction = context.newFunction('getJobArguments', () => {
      const list =
        typeof notebookVariablesService?.variableList$ === 'function'
          ? notebookVariablesService.variableList$()
          : notebookVariablesService?.variableList;
      const args = list
        ? SimpleUIService.resolveJobArgumentsFromVariables(list)
        : {};
      return JavaScriptHelper.createContextEntity(context, args);
    });
    context.setProp(context.global, 'getJobArguments', getJobArgumentsFunction);
    getJobArgumentsFunction.dispose();
    return context;
  }

  /**
   * Build the QuickJS `data` global from workflow variables underlaid with
   * project constants (SCRUM-626): `project constant < workflow variable`,
   * mirroring enoch's headless merge.
   */
  public static addGlobalData(
    context: any,
    chunkData: Chunk | null,
    notebookVariablesService: any,
    projectVariablesService?: any
  ) {
    const dataMap = StepDataHelper.buildStepDataMap(
      projectVariablesService?.variableList,
      notebookVariablesService?.variableList,
      chunkData ? chunkData.sortOrder : null
    );

    const objectForKey = context.newObject();

    for (const [name, value] of Object.entries(dataMap)) {
      const resultEntity = JavaScriptHelper.createContextEntity(context, value);
      context.setProp(objectForKey, name, resultEntity);
      resultEntity.dispose();
    }

    context.setProp(context.global, 'data', objectForKey);

    objectForKey.dispose();

    return context;
  }

  static async processGlobalModules(
    content: string,
    fileService: FileService
  ): Promise<string> {
    const list: FileModule[] = await fileService.getFileModuleList([
      FILE_EXTENSIONS.js,
      FILE_EXTENSIONS.json,
    ]);
    const hasModules = Array.isArray(list) && list.length > 0;
    const hasGlobal =
      hasModules && list.some((fileModule: FileModule) => fileModule.isGlobal);
    let result: string = '';
    if (hasGlobal) {
      const promises = list.map((item: FileModule) => {
        return fileService.getLocalforageItem(item.name);
      });
      return await Promise.all(promises).then((results) => {
        for (let index = 0; index < list.length; index++) {
          result += results[index];
        }
        return `${result}
            ${content}`;
      });
    } else {
      return content;
    }
  }
}
